<script>
  import {createEventDispatcher, onMount, tick} from 'svelte';
  import createCategoriesStore from '../../stores/categories';
  import date from '../../functions-common/date';
  import math from '../../functions-common/math';
  import beatSaver from '../../functions-common/beatsaver';
  import db from '../../db';
  import Map from '../Map.svelte'
  import Confirm from '../Confirm.svelte'
  import Loader from '../Loader.svelte'

  const dispatch = createEventDispatcher();

  export let project;
  export let maps;

  let categories = createCategoriesStore();

  let map;
  let mapEl;
  let mapErrorMsg = "";
  let mapErrorTryAgain = false;

  let song = null;

  let category;
  let categoryEl;
  let categoryErrorMsg = "";

  let saveErrorMsg = "";
  let saveErrorTryAgain = false;

  let isLoading = false;
  let saveEnabled = false;
  let isSaving = false;
  let showCancelDialog = false;

  let loaderText = null;

  const getMaxScore = (blocks, maxScorePerBlock = 115) =>
    Math.floor(
      (blocks >= 14 ? 8 * maxScorePerBlock * (blocks - 13) : 0) +
      (blocks >= 6
        ? 4 * maxScorePerBlock * (Math.min(blocks, 13) - 5)
        : 0) +
      (blocks >= 2
        ? 2 * maxScorePerBlock * (Math.min(blocks, 5) - 1)
        : 0) +
      Math.min(blocks, 1) * maxScorePerBlock,
    )

  const sanitizeFoundMap = map => {
    if (!map) return null;

    const {category_id, category_name, difficulty, map_id, ...sanitizedMap} = map;

    return sanitizedMap;
  }

  const findMapByHash = (maps, hash) => {
    if (!maps || !maps.length || !hash) return null;

    return sanitizeFoundMap(maps.find(m => m.hash === hash));
  }

  const findMapByKey = (maps, key) => {
    if (!maps || !maps.length || !key) return null;

    return sanitizeFoundMap(maps.find(m => m.data && m.data.key === key));
  }

  const getSongDiffs = song => {
    if (!song || !song.data || !song.data.versions || !song.data.versions.length ||
      !song.data.versions[0].diffs || !Array.isArray(song.data.versions[0].diffs)
    )
      return [];

    const standardDiffs = song.data.versions[0].diffs.filter(c => c.characteristic === 'Standard');
    if (!standardDiffs || !standardDiffs.length) return [];

    return standardDiffs
      .reduce((cum, diff) => {
        if (!diff.difficulty || !diff.difficulty.length) return cum;

        const difficulty = diff.difficulty[0].toLowerCase() + diff.difficulty.substr(1);
        cum[difficulty] = {
          diff: difficulty,
          maxScore: diff.notes ? getMaxScore(diff.notes) : (diff.maxScore ? diff.maxScore : null),
        }

        return cum;
      }, {});
  }

  async function onMapInput() {
    let mapHash, mapKey;

    mapErrorMsg = "";
    mapErrorTryAgain = false;

    let match = map.match(/^\s*([a-f-A-F0-9]{40,})\s*$/);
    if (match) {
      mapHash = match[1].toLowerCase();
    }

    if (!mapHash && (match = map.match(/^https:\/\/beatsaver.com\/maps\/([a-fA-F0-9]+)/))) {
      mapKey = match[1].toLowerCase();
    }

    if (!mapHash && !mapKey) {
      mapErrorMsg = "Please enter valid song hash or Beat Saver URL";
      return;
    }

    let addedSong;
    if (mapHash) addedSong = findMapByHash(maps, mapHash);
    if (mapKey) addedSong = findMapByKey(maps, mapKey);

    if (!addedSong) {
      let bsData;
      try {
        loaderText = "Loading song...";

        if (mapHash) bsData = await beatSaver.byHash(mapHash);
        else bsData = await beatSaver.byKey(mapKey);
      } catch (err) {
        mapErrorMsg = `BeatSaverError: ${err.toString ? err.toString() : err}`
        mapErrorTryAgain = true;
        return;
      }
      finally {
        loaderText = null;
      }

      const {
        versions: {[0]: {hash, coverURL: cover_url}},
        metadata: {songName: name, songSubName: subName, songAuthorName: author_name, levelAuthorName: level_author},
      } = bsData;
      const newSong = {hash, name: `${name}${subName ? ' ' + subName : ''}`, author_name, level_author, cover_url, data: bsData}

      try {
        loaderText = "Saving song to DB...";

        const {data, error} = await db
          .from('songs')
          .upsert([{...newSong, hash: hash.toLowerCase()}], {onConflict: 'hash'});

        if (error) throw 'DB Error';

        if (data && data.length) newSong.song_id = data[0].song_id;

        addedSong = newSong;
      } catch {
        mapErrorMsg = `DB Error`
        mapErrorTryAgain = true;
        return;
      }
      finally {
        loaderText = null;
      }
    }

    const allSongDiffs = getSongDiffs(addedSong);
    const alreadyUsedDiffs = maps ? maps.filter(m => m.hash === addedSong.hash).map(m => m.difficulty) : [];
    const availableDiffs = Object.keys(allSongDiffs).filter(diff => !alreadyUsedDiffs.includes(diff));

    if (!availableDiffs || !availableDiffs.length) {
      mapErrorMsg = "All difficulty levels of this song have already been added to the project.";
      return;
    }

    const {song_id, map_id, hash, name, author_name, level_author, cover_url, data} = addedSong;
    const duration = data && data.metadata && data.metadata.duration ? date.formatDuration(data.metadata.duration, 'long') : '-';

    song = {
      song_id, map_id, hash, name, author_name, level_author, cover_url, data, duration,
      allSongDiffs,
      availableDiffs,
      difficulty: null,
    };
  }

  async function onCategoryInput() {
    await tick();

    categoryErrorMsg = "";
    saveEnabled = false

    if (!song.category_id || !song.category_id.length) {
      if (categoryEl) categoryEl.focus();
    } else {
      category = "";
    }

    if ((!song.category_id || !song.category_id.length) && (!category || !category.length)) {
      categoryErrorMsg = "Please select category or enter a new one";
      return;
    }

    if (
      (!song.category_id || !song.category_id.length) &&
      $categories.map(c => c.name.toLowerCase()).includes(category.toLowerCase())
    ) {
      categoryErrorMsg = `Category "${category}" already exists, please select it from the list.`;
      return;
    }

    saveEnabled = true;
  }

  async function onSave() {
    saveErrorMsg = "";
    saveErrorTryAgain = false;

    try {
      if (!song.category_id || !song.category_id.length) {
        loaderText = "Saving category to DB...";

        const {data, error} = await db
          .from('categories')
          .upsert([{name: category.toLowerCase()}], {onConflict: 'name'});

        if (error) throw 'DB Error';

        if (data && data.length) song.category_id = data[0].category_id;
      }

      if (!song.map_id || !song.map_id.length) {
        const mapData = {
          song_id: song.song_id,
          difficulty: song.difficulty,
          category_id: song.category_id,
          max_score: song.difficulty && song.allSongDiffs[song.difficulty] && song.allSongDiffs[song.difficulty].maxScore ? song.allSongDiffs[song.difficulty].maxScore : null,
        }

        loaderText = "Saving map to DB...";

        const {data: existingMapData, error: existingMapError} = await db
          .from('maps')
          .select('map_id')
          .eq('song_id', mapData.song_id)
          .eq('difficulty', mapData.difficulty)
          .eq('category_id', mapData.category_id)
        ;

        if (existingMapError) throw 'DB Error';

        if (existingMapData && existingMapData.length) {
          song.map_id = existingMapData[0].map_id;
        } else {
          const {data, error} = await db
            .from('maps')
            .upsert([mapData]);

          if (error) throw 'DB Error';

          if (data && data.length) song.map_id = data[0].map_id;
        }

        const {error} = await db
          .from('project_maps')
          .upsert([{project_id: project.project_id, map_id: song.map_id}]);

        if (error) throw 'DB Error'
      }

      dispatch('add')
    } catch (err) {
      saveErrorMsg = `DB Error`
      saveErrorTryAgain = true;

      return;
    }
    finally {
      loaderText = null;
    }
  }

  function onShowCancelDialog() {
    showCancelDialog = true;
  }

  function onCancelEditing() {
    showCancelDialog = false;

    dispatch('cancel')
  }

  onMount(() => {
    if (mapEl) mapEl.focus();
  })
</script>

{#if project}
  <Loader text={loaderText} />

  <section class="add-song">
    {#if !song}
      <label for="map">Song hash OR Beat Saver URL</label>
      <input type="text" id="map" class="nes-input" class:pulse={isLoading} disabled={isLoading}
             bind:this={mapEl} bind:value={map}
             on:input={onMapInput} on:focus={() => mapEl && mapEl.select()}
      >
      <div class="nes-text is-error">
        {mapErrorMsg}
        {#if mapErrorTryAgain}<a on:click={onMapInput}>Try again</a>{/if}
      </div>
    {:else}
      <Map map={song}>
        <div class="song-info" slot="footer">
          <div>{song.duration}</div>
          <div>{
            song.difficulty && song.allSongDiffs[song.difficulty] && song.allSongDiffs[song.difficulty].maxScore
              ? math.formatNumber(song.allSongDiffs[song.difficulty].maxScore, 0) + ' max pts'
              : ''
          }</div>
        </div>
      </Map>

      <div class="nes-select">
        <select bind:value={song.difficulty}>
          <option value="">Select difficulty...</option>
          {#each song.availableDiffs as diff}
            <option value={diff}>{diff}</option>
          {/each}
        </select>
      </div>

      {#if song.difficulty}
        <div class="nes-select">
          <select bind:value={song.category_id} on:input={onCategoryInput}>
            <option value="">Select category...</option>
            {#each $categories as cat}
              <option value={cat.category_id}>{cat.name}</option>
            {/each}
          </select>
        </div>

        <label for="new_category">...or add a new one:</label>
        <input type="text" id="new_category" class="nes-input" class:pulse={isLoading}
               disabled={isLoading || (song.category_id && song.category_id.length)}
               bind:this={categoryEl} bind:value={category} on:input={onCategoryInput}
        >
        <div class="nes-text is-error">
          {categoryErrorMsg}
        </div>
      {/if}
    {/if}
  </section>

  <div class="actions">
    <button class={`nes-btn is-primary ${!saveEnabled || isSaving ? 'is-disabled' : ''}`}
            disabled={!saveEnabled || isSaving}
            on:click={onSave}
    >Save
    </button>
    <button class="nes-btn is-error" on:click={onShowCancelDialog}>Cancel and back to the project</button>

    <div class="nes-text is-error">
      {saveErrorMsg} {#if saveErrorTryAgain}<a on:click={onSave}>Try again</a>{/if}
    </div>
  </div>

  <Confirm title="Warning" desc="Are you sure you want to abandon editing?" bind:show={showCancelDialog}
           on:confirm={onCancelEditing} on:cancel={() => showCancelDialog = false}
  />
{/if}

<style>
    .add-song {
        color: #222;
        background-color: #aaa;
        padding: .5rem;
        margin-bottom: 2rem;
    }

    .song-info {
        text-align: right;
    }

    .actions {
        margin-top: 1rem;
    }

    .nes-select {
        margin-bottom: 1rem;
    }

    .nes-input, .nes-select select {
        background-color: #aaa;
    }

    .nes-text.is-error {
        margin-top: .5rem;
    }

    .pulse {
        animation: pulse 1.5s infinite;
    }

    @keyframes pulse {
        0% {
            background-color: #212529;
        }

        50% {
            background-color: #aaa;
        }

        100% {
            background-color: #212529;
        }
    }
</style>